import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Code2, Settings2 } from 'lucide-react';
import { useState } from 'react';
import { Json } from '@/integrations/supabase/types';

interface PromptViewerProps {
  taskName: string;
  prompt: string;
  config?: Json | Record<string, any> | null | undefined;
  scheduleInfo?: {
    time: string;
    dayOfWeek: string;
  };
}

export function PromptViewer({ taskName, prompt, config, scheduleInfo }: PromptViewerProps) {
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  // Convert Json to Record<string, any> if needed
  const configObj = config && typeof config === 'object' && !Array.isArray(config) 
    ? config as Record<string, any>
    : undefined;

  return (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Code2 className="h-4 w-4" />
          Tekniske detaljer
        </CardTitle>
        <CardDescription>
          Vis nåværende prompt og innstillinger for {taskName}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Prompt Section */}
        <Collapsible open={isPromptOpen} onOpenChange={setIsPromptOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-3 h-auto">
              <span className="font-medium">AI-prompt</span>
              {isPromptOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="p-4 rounded-lg bg-muted/50 border">
              <pre className="text-xs whitespace-pre-wrap font-mono overflow-x-auto">
                {prompt}
              </pre>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Config Section */}
        {(configObj || scheduleInfo) && (
          <Collapsible open={isConfigOpen} onOpenChange={setIsConfigOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between p-3 h-auto">
                <span className="font-medium flex items-center gap-2">
                  <Settings2 className="h-4 w-4" />
                  Konfigurasjon
                </span>
                {isConfigOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="p-4 rounded-lg bg-muted/50 border space-y-3">
                {scheduleInfo && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      Publiseringsplan
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Dag:</span>
                        <span className="font-medium">{scheduleInfo.dayOfWeek}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tid:</span>
                        <span className="font-medium">{scheduleInfo.time}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {configObj && Object.keys(configObj).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      Innstillinger
                    </h4>
                    <div className="space-y-1 text-sm">
                      {Object.entries(configObj).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-start gap-4">
                          <span className="text-muted-foreground break-words">{key}:</span>
                          <span className="font-medium text-right break-all max-w-[60%]">
                            {typeof value === 'object' 
                              ? JSON.stringify(value, null, 2)
                              : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
